import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { FileText, Download, Clock, CheckCircle, ArrowLeft, ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from '@/components/ui/dialog';
import { useNavigate } from 'react-router-dom';

const PrivateTransportationAuthority = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen">
      <Header />
      
      <section className="pt-32 pb-20 bg-background">
        <div className="max-w-4xl mx-auto px-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/diy-processes')}
            className="mb-8 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to DIY Processes
          </Button>

          <div className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="p-4 rounded-xl bg-primary/10">
                <FileText className="h-12 w-12 text-primary" />
              </div>
            </div>
            <h1 className="font-rockwell text-4xl md:text-5xl font-semibold text-primary mb-6">
              Establish Private Transportation Authority
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
              Learn how to establish your authority for private transportation. 
              Understand the legal foundations of traveling versus driving.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-card border border-border rounded-lg p-6 text-center">
              <Clock className="h-8 w-8 text-primary mx-auto mb-3" />
              <h3 className="font-semibold text-foreground mb-2">Time Estimate</h3>
              <p className="text-muted-foreground">one hour</p>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-6 text-center">
              <Download className="h-8 w-8 text-primary mx-auto mb-3" />
              <h3 className="font-semibold text-foreground mb-2">Template</h3>
              <p className="text-muted-foreground">1 template included</p>
            </div>
          </div>

          <div className="bg-card border border-border rounded-xl p-8 mb-8">
            <h2 className="font-rockwell text-2xl font-semibold text-foreground mb-6">
              What You'll Learn
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-foreground mb-3">Essential Knowledge</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-1 flex-shrink-0" />
                    Right to travel vs driving privilege distinction
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-1 flex-shrink-0" />
                    Private vs commercial transportation law
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-1 flex-shrink-0" />
                    Constitutional protections and precedents
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-3">Practical Skills</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-1 flex-shrink-0" />
                    Notice and declaration procedures
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-1 flex-shrink-0" />
                    Proper documentation and recording methods
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2 mt-1 flex-shrink-0" />
                    Authority establishment protocols
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="text-center mb-8">
            <h2 className="font-rockwell text-2xl font-semibold text-foreground underline">
              Start below
            </h2>
          </div>

          {/* Step Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Step 1 */}
            <div className="group cursor-pointer" onClick={() => navigate('/video-library/driving')}>
              <div className="bg-card border border-border rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-primary/50 h-[200px] flex flex-col">
                <div className="flex-1">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 mb-4">
                    <span className="text-xl font-bold text-primary">1</span>
                  </div>
                  <h3 className="font-rockwell text-lg font-semibold text-foreground mb-3 leading-tight">
                    Understanding Travel vs Driving
                  </h3>
                </div>
                <Button variant="outline" size="sm" className="w-full group-hover:border-primary/50 transition-colors">
                  Start Step 1
                </Button>
              </div>
            </div>

            {/* Step 2 */}
            <Dialog>
              <DialogTrigger asChild>
                <div className="group cursor-pointer">
                  <div className="bg-card border border-border rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-primary/50 h-[200px] flex flex-col">
                    <div className="flex-1">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 mb-4">
                        <span className="text-xl font-bold text-primary">2</span>
                      </div>
                      <h3 className="font-rockwell text-lg font-semibold text-foreground mb-3 leading-tight">
                        Getting Your Free United States Department of Transportation Authority
                      </h3>
                    </div>
                    <Button variant="outline" size="sm" className="w-full group-hover:border-primary/50 transition-colors">
                      Start Step 2
                    </Button>
                  </div>
                </div>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] md:max-h-[85vh] overflow-y-auto mx-2 md:mx-8 my-2 md:my-4 w-[95vw] md:w-auto [&>button]:hidden">
                <DialogHeader className="relative pb-4 border-b border-border/20">
                  <DialogClose asChild>
                    <Button
                      variant="outline"
                      className="absolute right-0 top-0 h-12 w-20 rounded-lg bg-background/95 hover:bg-primary/5 hover:text-primary transition-all duration-300 hover:scale-[1.02] border-2 border-border/40 hover:border-primary/40 shadow-sm hover:shadow-md backdrop-blur-sm group"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1.5 group-hover:-translate-x-0.5 transition-transform duration-200" />
                      <span className="text-sm font-semibold">Back</span>
                    </Button>
                  </DialogClose>
                  <DialogTitle className="text-lg md:text-2xl font-bold text-primary leading-tight pr-24">
                    Step 2: Getting Your Free United States Department of Transportation Authority
                  </DialogTitle>
                </DialogHeader>
                <div className="text-foreground mt-4 md:mt-6 text-sm md:text-base">
                  <div className="space-y-6 md:space-y-8">
                    <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 md:p-6">
                      <p className="text-muted-foreground italic">Content will be available soon. This step covers the process of obtaining your free United States Department of Transportation authority, including notice and declaration procedures, proper documentation and recording methods, and authority establishment protocols.</p>
                    </div>
                  </div>
                  <div className="h-16"></div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default PrivateTransportationAuthority;